// Util
import { vuelidateUtil } from '@/utils/vuelidate-util.js';
import { v4 as uuidV4 } from 'uuid';

// Third party libraries
import DatePicker from 'vue2-datepicker';
import { required, requiredIf } from 'vuelidate/lib/validators';

// Services
import breedService from '@/app/services/main/breed/breed-service.js';

// Mixins
import { GoogleAnalyticsMixins } from '@/app/mixins/utils/google-analytics-mixins';
import { toastMessageMixins } from '@/app/mixins/utils/toast-message-mixins';
import { imageCompressorMixins } from '@/app/mixins/utils/image-compressor-mixins';

// Constants
import { GA } from '@/app/constants/ga-constants';
import { STATUS_CODES } from '@/app/constants/status-constants';

// Third Party Libraries
import { mapState } from 'vuex';

// Components
const FileUpload = () => import(/* webpackPrefetch: true, webpackChunkName: "file-upload" */ '@/app/views/main-view/_shared/_components/file-upload/file-upload.vue');

export default {
	name: 'AddPet',
	components:{
		DatePicker,
		FileUpload
	},
	mixins: [
		toastMessageMixins,
		GoogleAnalyticsMixins,
		imageCompressorMixins
	],
	props: {
		isModal : {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			breedList: [],
			isAPILoading: false,
			addedBreedName: '',
			genderOptions: [
				{
					value: 'M',
					option: 'Male'
				},
				{
					value: 'F',
					option: 'Female'
				}
			],
			breedSizes: [
				{ id: 0, name: 'Small', value: '2'},
				{ id: 1, name: 'Medium', value: '3'},
				{ id: 2, name: 'Large', value: '4'}
			],
			imageToCrop: '',
			// roleOptions: [
			// 	{
			// 		value: 1,
			// 		option: 'Pet Owner'
			// 	},
			// 	{
			// 		value: 4,
			// 		option: 'Walker'
			// 	},
			// 	{
			// 		value: 5,
			// 		option: 'Sitter'
			// 	},
			// 	{
			// 		value: 6,
			// 		option: 'Walker and Sitter'
			// 	}
			// ]
		}
	},
	computed: {
		...mapState({
			petDetails: state => state.registrationPetDetails.petDetails,
			imageSrc: state => state.registrationPetDetails.imageSrc
		}),
		validationRules() {
			let fields = {
				petDetails: {
					name: { required },
					breedId: { required },
					breedSize: { required: requiredIf(function() {
						return parseInt(this.petDetails.breedId) <= 0
					})},
					birthdate: { required },
					gender: { required },
				}
			}
			
			// fields.petDetails = vuelidateUtil.setFieldsRequired(fields.petDetails, true);
			fields.petDetails = vuelidateUtil.setPetNameLength(fields.petDetails, 'name');

			return fields;
		},
		hasUploaded() {
			let img = '';

			if (this.imageSrc) {
				img = this.imageSrc;
			} else {
				img = require('@/assets/images/_shared/default-profile-pic.png')
			}

			return img;
		},
		selectedGender() {
			let gender = '';
			
			if (this.petDetails.gender) {
				gender = this.petDetails.gender === 'M' ? 'Male' : 'Female'
			} else {
				gender = 'Gender';
			}

			return gender;
		},
		// selectedRole() {
		// 	let selectedRole = this.roleOptions.find(roles => {
		// 		return roles.value === this.petDetails.role;
		// 	});
			
		// 	return selectedRole.option;
		// }
	},
	watch: {
		'$v.$invalid' : {
			immediate: true,
			deep: true,
			handler(newValue) {
				this.$emit('enableAddPet', newValue);
			}
		},
		isAPILoading(newValue) {
			this.$emit('apiLoadingDone', newValue);
		}
	},
	created() {
		this.$store.commit('clearAddPetDetails');
		this.getBreedList();
		if (parseInt(this.petDetails.breedId) === 0) {
			this.petDetails.breedId = this.petDetails.breedId.split('-')[1].trim('')
		}
		if (!this.petDetails.breedSize) {
			this.petDetails.breedSize = 0
		}
	},
	methods: {
		checkOptionID(option) {
			if (typeof option.id === 'string') {
				return option.id ? option.id.substring(0, 2) : false;
			}
			return false;
		},
		createOptionBreed(breed) {
			this.petDetails.breedName = breed;
			return { name: breed, id: `0-${breed}` }
		},
		getBreedList() {
			this.isAPILoading = true;
			breedService.getBreeds()
				.then(res => {
					if (res.status === STATUS_CODES.SUCCESS) {
						this.isAPILoading = false;
						this.breedList = res.data.data.filter(dt => dt.id != 0)
						this.$store.commit('setBreedLists', this.breedList);
					} else {
						const { ERROR_GET_BREED_LIST } = this.toastMessages.breed();
						this.showAPIErrorToast(ERROR_GET_BREED_LIST, res);
					}
				});
		},
		async onDonePet() {
			if (this.$v.$invalid) {
				return;
			}

			// compress pet image if more than 2mb
			if (this.petDetails && this.petDetails.petImageUpload) {
				const petName = this.petDetails.name;
				const originalPetImageFile = this.petDetails.petImageUpload[0];

				// Extract file extension from the original file name
				const fileExtension = originalPetImageFile.name.split('.').pop();

				// shorten file name and to avoid too long name in the db that may cause error
				const newFileName = `${petName}-${uuidV4()}.${fileExtension}`;
				const modifiedFile = new File([originalPetImageFile], newFileName, { type: originalPetImageFile.type });

				this.petDetails.petImageUpload = await this.compressImage([modifiedFile]);
			}

			// set all data to Store to be handled in Add Human for registration
			this.$store.commit('setAddPetDetails', this.petDetails);

			// send Google Analytics
			this.sendEventTrackingToGA(GA.REGISTRATION_PAGE.NEXT_BUTTON.ACTION, GA.REGISTRATION_PAGE.CATEGORY, GA.REGISTRATION_PAGE.NEXT_BUTTON.LABEL);

			const component = this.petDetails.role === 1 ? 'add-human' : 'add-human-walker-sitter';
			
			this.$emit('setActiveComponent', component);
		},
		async onUploadImage(event) {
			let valid = await this.validateImage(event.target.files[0]);
			
			// do nothing when user cancels Upload
			if(!valid) {
				// reset uploader
				this.$refs.imgUploader.value = '';
				return;
			}

			// compress image if more than 2mb
			const file = await this.compressImage(event.target.files[0]);

			this.petDetails.petImageUpload = [file];
			// show preview of pet image 
			let reader = new FileReader();

			reader.onload = () => {
				this.imageToCrop = reader.result;
				this.$refs.modalFileUpload.show();
			}

			this.sendEventTrackingToGA(GA.REGISTRATION_PAGE.ADD_PET_IMAGE.ACTION, GA.REGISTRATION_PAGE.CATEGORY, GA.REGISTRATION_PAGE.ADD_PET_IMAGE.LABEL);
			
			reader.readAsDataURL(file); 
		},
		notAfterToday(date) {
			return date > new Date();
		},
		onBreedBlur() {
			this.$v.petDetails.breedId.$touch();
		},
		onSelectedGender(gender) {
			this.petDetails.gender = gender.value;
		},
		clearFields() {
			this.$refs.imgUploader.value = '';
			this.$store.commit('clearAddPetDetails');
			this.$v.$reset();
		},
		setImageCrop({image, blob}) {
			this.$store.commit('setImageSrc', image);
			this.petDetails.petImageUpload = blob;
		},
		clearFileInput(event) {
			event.target.value = '';
		},
		// onSelectedRole(role) {
		// 	this.petDetails.role = role;
		// },
		// 	skipAddingPet() {
		// 		this.$swal({
	//               imageHeight: '30px',
	//               title: `A default pet will be registered to your account.`,
	//               imageUrl: `${require('@/assets/images/_shared/dog-confirm.png')}`,
	//               confirmButtonText: 'Confirm',
	//               showCancelButton: true,
	//               cancelButtonText: 'Cancel',
	//               reverseButtons: true
	//           })
	//           .then(result => {
	//               if (result) {
		// 				// set all data to Store to be handled in Add Human for registration
		// 				this.$store.commit('setAddPetDetails', this.petDetails);
		// 				// send Google Analytics
		// 				this.sendEventTrackingToGA(GA.REGISTRATION_PAGE.NEXT_BUTTON.ACTION, GA.REGISTRATION_PAGE.CATEGORY, GA.REGISTRATION_PAGE.NEXT_BUTTON.LABEL);
		// 				this.$emit('setActiveComponent', 'add-human-walker-sitter');
		// 			}
	//           });
		// 	}
		},
	validations() {
		return this.validationRules;
	}
}